import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "../clients";

const fetchSegmentList = async ({ query = {} }) => {
  return client("/segment_list", {
    method: "POST",
    data: query,
  }).then((data: any) => data);
};

interface SegmentQueryPayload {
  country: string;
  tags?: string[];
}

const useSegmentList = ({ query = {}, options }: any = {}) => {
  const payload : SegmentQueryPayload = {
    country: query.countryName || query.country,
    tags: query.tags,
  };
  return useQuery({
    queryKey: ["segment-list", payload],
    queryFn: () => fetchSegmentList({ query:payload }),
    ...options,
  }) as any;
};

function useCreateCampaign({ options }: any) {
  return useMutation({
    mutationFn: (reqBody: any) =>
      client("/campaign", {
        method: "POST",
        data: reqBody,
      }),
    ...options,
  }) as any;
}

function useUpdateCampaignEndDate({ options }: any) {
  return useMutation({
    mutationFn: (reqBody: any) =>
      client("/update_campaign_enddate", {
        method: "POST",
        data: reqBody,
      }),
    ...options,
  }) as any;
}

type CampaignDetailQuery = {
  campaignId?: string;
  tags?: string[];
  countries?: string[];
  simulateStatus?: number;
};

const fetchGetCampaignDetail = async ({ query = {} as CampaignDetailQuery }) => {
  const res = await client("/fetch_campaign", {
    method: "POST",
    data: {
      ...query,
    },
  }).then((data: any) => data);
  console.log(query.simulateStatus,res);
  if (typeof query.simulateStatus !== "undefined") {
    const status = Number(query.simulateStatus);
    const err: any = new Error("Simulated error");
    err.response = { status, data: { message: "Simulated error" } };
    throw err;
  }
  if (res?.isAxiosError || (res?.status && res.status >= 400)) {
    throw res;
  }
  return res;
};

/**
 * Retrieves campaign details using the /fetch_campaign endpoint.
 *
 * @param query - Campaign detail request parameters
 * @param query.campaignId - Campaign identifier
 * @param query.tags - Optional array of tags to filter results
 * @param query.countries - Optional array of countries to filter results
 * @param query.simulateStatus - Optional HTTP status code to simulate an error (testing only)
 *
 * @param options - React Query options (e.g., enabled, select, onError, staleTime, gcTime)
 *
 * @returns React Query result for campaign detail request
 *
 * @remarks
 * - Cache key: ["fetch-campaign", query] — cache is keyed by full query payload
 * - Retries: disabled (`retry: false`) to avoid repeated failing requests
 * - Error simulation: set `query.simulateStatus` to force a specific HTTP error for testing flows
 *
 * @example
 * const { data, isPending, error } = useGetCampaignDetail({
 *   query: { campaignId, tags, countries },
 *   options: { enabled: !!campaignId }
 * });
 */
const useGetCampaignDetail = ({ query = {}, options }: any = {}) => {
  //query.simulateStatus = 500;
  return useQuery({
    queryKey: ["fetch-campaign", query],
    queryFn: () => fetchGetCampaignDetail({ query }),
    retry: false,
    ...options,
  }) as any;
};

const fetchGetDynamicFields = async ({ query = {} }) => {
  return client("/dynamic_fields", {
    method: "POST",
    data: {
      ...query,
    },
  }).then((data: any) => data);
};

const useGetDynamicFields = ({ query = {}, options }: any = {}) => {
  return useQuery({
    queryKey: ["dynamic-fields", query],
    queryFn: () => fetchGetDynamicFields({ query }),
    ...options,
  }) as any;
};

const fetchCampaignDropdown = async ({ query = {} }) => {
  return client("/campaign_dropdown", {
    method: "POST",
    data: query,
  }).then((data: any) => data);
};

const useCampaignDropdown = ({ query = {}, options }: any = {}) => {
  return useQuery({
    queryKey: ["campaign-dropdown", query],
    queryFn: () => fetchCampaignDropdown({ query }),
    ...options,
  }) as any;
};

const fetchCampaignsHistory = async ({ query = {} }) => {
  return client("/campaign_history", {
    method: "POST",
    data: query,
  }).then((data: any) => {
    return data;
  });
};

const useCampaignsHistory = ({ query = {}, options }: any = {}) => {
  return useQuery({
    queryKey: ["campaign_history", query],
    queryFn: () => fetchCampaignsHistory({ query }),
    ...options,
  }) as any;
};

const fetchCampaignExecutionLog = async ({ query = {} }) => {
  return client("/campaign_execution_log", {
    method: "POST",
    data: query,
  }).then((data: any) => data);
};

const useCampaignExecutionLog = ({ query = {}, options }: any = {}) => {
  return useQuery({
    queryKey: ["campaign_execution_log", query],
    queryFn: () => fetchCampaignExecutionLog({ query }),
    ...options,
  }) as any;
};

export {
  useCampaignDropdown,
  useCreateCampaign,
  useGetCampaignDetail,
  useGetDynamicFields,
  useSegmentList,
  useUpdateCampaignEndDate,
  useCampaignsHistory,
  useCampaignExecutionLog
};
